import Component from '@glimmer/component';
import { waitForPromise } from '@ember/test-waiters';
import { modifier } from 'ember-modifier';

// PLAYER_EVENTS is the set of video.js player events that can be forwarded to
// the consumer. When an argument matching one of these names is passed as a
// closure (e.g. `@timeupdate={{this.onTimeupdate}}`), it is invoked with
// `(player, component, ...eventArgs)` whenever that event fires. This preserves
// the event API of the original ember-videojs addon.
const PLAYER_EVENTS = [
  'abort',
  'canplay',
  'canplaythrough',
  'durationchange',
  'emptied',
  'ended',
  'error',
  'loadeddata',
  'loadedmetadata',
  'loadstart',
  'pause',
  'play',
  'playing',
  'progress',
  'ratechange',
  'resize',
  'seeked',
  'seeking',
  'stalled',
  'suspend',
  'timeupdate',
  'useractive',
  'userinactive',
  'volumechange',
  'waiting',
  // @queenvictoria added (retained from the original addon)
  'click',
  'tap',
];

// VideojsPlayer wraps a video.js player as a single Glimmer component,
// consolidating the original `videojs-base` (logic) and `videojs-player`
// (template) classic components.
//
// video.js takes ownership of the DOM: initialising it against a <video>
// element inserts a generated wrapper and moves the <video> inside it. The
// template wraps the <video> in a stable outer <div> so Glimmer's component
// root is never the element video.js relocates. The <video> carries no dynamic
// child blocks (sources and tracks are applied through the player API), so
// Glimmer never reconciles children that video.js has moved.
export default class VideojsPlayer extends Component {
  // The video.js player instance, created once the <video> element is inserted.
  player = null;

  // Serialised snapshot of the source list currently applied to the player, so
  // updateSource can skip redundant reloads. The initial sources are applied
  // when the player is built, so we only act on subsequent changes.
  appliedSourcesKey = null;

  constructor(owner, args) {
    super(owner, args);

    // Snapshot the initial configuration up-front. The setup modifier below
    // must build the player without reading `this.args` inside its tracked
    // execution frame -- doing so would subscribe it to argument changes and
    // cause Glimmer to tear down and recreate the player on every @src change.
    // Reactive source changes are handled by updateSource instead.
    this.appliedSourcesKey = JSON.stringify(this.buildSources());
    this.trackSpecs = this.buildTrackSpecs();
    this.playerOptions = this.buildOptions();
  }

  // controls defaults to true to match the original component; only an explicit
  // @controls={{false}} hides them.
  get controls() {
    return this.args.controls ?? true;
  }

  // setupPlayer initialises the player against the <video> element the template
  // rendered, and disposes it on teardown. It reads only plain (untracked)
  // fields, so it runs exactly once per element lifecycle -- mirroring the old
  // didInsertElement / willDestroyElement pairing. The standard media
  // attributes (controls, autoplay, etc.) are bound declaratively in the
  // template and read by video.js off the element.
  //
  // video.js and its stylesheet are large, so they load when the first player
  // renders. waitForPromise makes settled() wait for the load in tests.
  setupPlayer = modifier((videoEl) => {
    let disposed = false;

    waitForPromise(
      Promise.all([import('video.js'), import('video.js/dist/video-js.css')]),
    ).then(([{ default: videojs }]) => {
      if (disposed) {
        return;
      }
      const player = videojs(videoEl, this.playerOptions);

      // Event listeners can be attached before the player is ready.
      this.registerEventForwarders(player);

      player.ready(() => {
        // Text tracks are registered through the player API rather than via
        // <track> children or the `tracks` option, both of which video.js only
        // picks up once a source loads. addRemoteTextTrack requires the tech, so
        // it runs once the player is ready.
        for (const spec of this.trackSpecs) {
          player.addRemoteTextTrack(spec, false);
        }

        if (typeof this.args.ready === 'function') {
          this.args.ready(player, this);
        }
      });

      this.player = player;

      // updateSource skips source changes until the player exists. Apply a
      // change that arrived during the load. This callback runs outside the
      // tracking frame, so it can read the arguments.
      const list = this.buildSources();
      const key = JSON.stringify(list);
      if (key !== this.appliedSourcesKey) {
        this.appliedSourcesKey = key;
        if (list.length) {
          player.src(list);
        } else {
          player.reset();
        }
      }
    });

    return () => {
      disposed = true;
      this.player?.dispose();
      this.player = null;
    };
  });

  // updateSource reacts to changes of @src, @type and @sources once the player
  // exists, mirroring the original component's updatePlayer(): pause, swap
  // source, reload. The full source list is diffed so a changed array or MIME
  // type is picked up; an empty list is treated as an explicit clear so stale
  // media is not left behind.
  updateSource = modifier((element, [src, type, sources]) => {
    const list = this.buildSources(src, type, sources);
    const key = JSON.stringify(list);
    if (!this.player || key === this.appliedSourcesKey) {
      return;
    }

    this.appliedSourcesKey = key;
    this.player.pause();
    if (list.length) {
      this.player.src(list);
      this.player.load();
    } else {
      // An empty source list clears the media. reset() returns the player to
      // its initial, source-less state. appliedSourcesKey is updated above
      // regardless, so restoring a previously-cleared source re-applies it
      // rather than being skipped by the equality check.
      this.player.reset();
    }
  });

  // buildOptions maps the component arguments to a video.js options object.
  buildOptions() {
    const { args } = this;
    const options = {
      html5: args.html5 || {},
    };

    if (args.fluid) {
      options.fluid = args.fluid;
    }
    if (args.aspectRatio) {
      options.aspectRatio = args.aspectRatio;
    }
    if (args.width) {
      options.width = args.width;
    }
    if (args.height) {
      options.height = args.height;
    }
    if (args.liveui) {
      options.liveui = true;
    }

    const sources = this.buildSources();
    if (sources.length) {
      options.sources = sources;
    }

    return options;
  }

  // buildSources collects @src/@type and @sources into video.js source objects.
  // The inputs are passed explicitly so the update modifier can react to them;
  // they default to the current arguments for construction-time use.
  buildSources(
    src = this.args.src,
    type = this.args.type,
    sources = this.args.sources,
  ) {
    const result = [];

    if (src) {
      result.push(type ? { src, type } : { src });
    }
    if (sources) {
      result.push(...sources);
    }

    return result;
  }

  // buildTrackSpecs collects @textTrack and @textTracks into <track> attribute
  // maps. @textTrack mirrors the original addon's fixed English captions track;
  // @textTracks is the general list form.
  buildTrackSpecs() {
    const { textTrack, textTracks } = this.args;
    const specs = [];

    if (textTrack) {
      specs.push({
        kind: 'captions',
        src: textTrack,
        srclang: 'en',
        label: 'English',
      });
    }
    if (textTracks) {
      specs.push(
        ...textTracks.map((track) => ({
          kind: track.kind,
          src: track.src,
          srclang: track.language,
          label: track.label,
          type: track.type,
        })),
      );
    }

    return specs;
  }

  // registerEventForwarders wires each supported player event to the matching
  // closure argument, when the consumer supplied one.
  registerEventForwarders(player) {
    for (const eventName of PLAYER_EVENTS) {
      // eventName comes from the hardcoded PLAYER_EVENTS list, not user input.
      // eslint-disable-next-line security/detect-object-injection
      const handler = this.args[eventName];
      if (typeof handler !== 'function') {
        continue;
      }

      player.on(eventName, (...eventArgs) => {
        handler(player, this, ...eventArgs);
      });
    }
  }
}
